import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import clsx from 'clsx';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

type SettingsTab = 'profile' | 'account' | 'notifications' | 'security' | 'billing';

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'profile', label: 'Profile', icon: UserCircleIcon, description: 'Manage your personal information' },
  { id: 'account', label: 'Account', icon: Cog6ToothIcon, description: 'Configure account preferences' },
  { id: 'notifications', label: 'Notifications', icon: BellIcon, description: 'Set up alerts and notifications' },
  { id: 'security', label: 'Security', icon: ShieldCheckIcon, description: 'Password and security settings' },
  { id: 'billing', label: 'Billing', icon: CreditCardIcon, description: 'Manage subscription and payments' },
];

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [checkoutCanceled, setCheckoutCanceled] = useState(false);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  // Handle Stripe checkout redirects
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setActiveTab('billing');
      setCheckoutSuccess(true);
      setSearchParams({});
      setTimeout(() => setCheckoutSuccess(false), 10000);
    } else if (searchParams.get('canceled') === 'true') {
      setActiveTab('billing');
      setCheckoutCanceled(true);
      setSearchParams({});
      setTimeout(() => setCheckoutCanceled(false), 8000);
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[44px]',
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}

            <div className="border-t border-slate-200 my-4" />

            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors min-h-[44px]"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              Sign Out
            </button>
          </nav>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {activeTab === 'profile' && <ProfileSettings user={user} />}
            {activeTab === 'account' && <AccountSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'billing' && <BillingSettings checkoutSuccess={checkoutSuccess} checkoutCanceled={checkoutCanceled} />}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ProfileSettingsProps {
  user: { email: string; role: string } | null;
}

function ProfileSettings({ user }: ProfileSettingsProps) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-900">Profile Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Update your personal information</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <button className="px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
              Change Avatar
            </button>
            <p className="text-xs text-slate-500 mt-2">JPG, GIF or PNG. Max 1MB.</p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">First Name</label>
            <input
              type="text"
              defaultValue="Demo"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Last Name</label>
            <input
              type="text"
              defaultValue="User"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
            <input
              type="email"
              defaultValue={user?.email || ''}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-500 cursor-not-allowed"
              disabled
            />
            <p className="text-xs text-slate-500 mt-1">Contact support to change your email</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
            <input
              type="text"
              defaultValue="Demo Restaurant Group"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4">
          {saved && (
            <span className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckIcon className="w-4 h-4" />
              Changes saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountSettings() {
  return (
    <div>
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-900">Account Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Configure your account preferences</p>
      </div>

      <div className="p-6 space-y-6">
        <SettingToggle
          title="Demo Mode"
          description="Show sample data for evaluation. Turn off to see only your real orders and disputes."
          defaultEnabled={true}
        />
        <SettingToggle
          title="Auto-classify Orders"
          description="Automatically scan imported orders and flag suspicious refunds for review. Recommended."
          defaultEnabled={true}
        />
        <SettingToggle
          title="Auto-generate Drafts"
          description="Pre-write dispute messages for high-confidence cases. Review before submitting."
          defaultEnabled={false}
        />

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-medium text-slate-900 mb-2">Default Thresholds</h3>
          <p className="text-xs text-slate-500 mb-4">
            These thresholds determine when orders get flagged for review. Lower values = more sensitive flagging.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                B1: Item Missing Rate
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Flag when this % of order items are claimed missing
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  defaultValue={50}
                  className="w-24 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                B2: High Value Refund
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Flag when refund exceeds this % of order total
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  defaultValue={50}
                  className="w-24 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                B3: Full Refund Minimum
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Only flag full refunds on orders above this value
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">$</span>
                <input
                  type="number"
                  defaultValue={20}
                  className="w-24 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                B4: Repeat Offender Window
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Flag customers with 3+ refunds within this period
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  defaultValue={30}
                  className="w-24 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-500">days</span>
              </div>
            </div>
          </div>
        </div>

        {/* Agency Authorization Section */}
        <AgencyAuthorizationSection />
      </div>
    </div>
  );
}

function AgencyAuthorizationSection() {
  const [authorized, setAuthorized] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const handleAuthorize = () => {
    if (agreementAccepted) {
      setAuthorized(true);
      setShowAgreement(false);
      // In a real implementation, save this to the database
    }
  };

  return (
    <div className="border-t border-slate-200 pt-6">
      <h3 className="text-sm font-medium text-slate-900 mb-2">Agency Authorization</h3>
      <p className="text-xs text-slate-500 mb-4">
        Authorize RecoverRefunds to submit disputes on your behalf. Required for email-based dispute submission.
      </p>

      {authorized ? (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <CheckIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-900">Authorization Active</p>
            <p className="text-xs text-emerald-700 mt-1">
              RecoverRefunds is authorized to act as your agent for dispute submissions.
            </p>
            <button
              onClick={() => setAuthorized(false)}
              className="text-xs text-emerald-600 underline mt-2 hover:text-emerald-800"
            >
              Revoke Authorization
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Authorization Required</p>
              <p className="text-xs text-amber-700 mt-1">
                To use email-based dispute submission for Grubhub, you must authorize RecoverRefunds to act on your behalf.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowAgreement(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 transition-all"
          >
            Review & Accept Authorization Agreement
          </button>
        </div>
      )}

      {/* Authorization Agreement Modal */}
      {showAgreement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Agency Authorization Agreement</h3>
              <p className="text-sm text-slate-500 mt-0.5">Please review and accept the following terms</p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-sm max-w-none text-slate-600">
                <h4 className="text-slate-900 font-semibold">Authorization to Act as Agent</h4>
                <p>
                  By accepting this agreement, you ("Customer") hereby irrevocably designate and appoint
                  RecoverRefunds ("Agent") as Customer's attorney-in-fact, to act for and on Customer's behalf to:
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Submit dispute claims and communications to third-party delivery platforms including but not limited to Grubhub, DoorDash, and Uber Eats</li>
                  <li>Access account information necessary for dispute resolution</li>
                  <li>Receive and respond to communications regarding disputes on Customer's behalf</li>
                  <li>Provide documentation and evidence to support dispute claims</li>
                </ol>

                <h4 className="text-slate-900 font-semibold mt-6">Terms and Conditions</h4>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>This authorization remains in effect until revoked in writing by Customer</li>
                  <li>Customer retains ultimate responsibility for all disputes submitted</li>
                  <li>Agent will act in good faith and in Customer's best interests</li>
                  <li>Agent will maintain confidentiality of Customer's business information</li>
                  <li>Customer authorizes Agent to represent Customer's identity in communications with platforms</li>
                </ul>

                <h4 className="text-slate-900 font-semibold mt-6">Platform Compliance</h4>
                <p>
                  Customer acknowledges that some delivery platforms may have terms of service that restrict third-party
                  dispute submission. Customer agrees to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Accept responsibility for any platform policy violations</li>
                  <li>Indemnify Agent against claims arising from authorized dispute activities</li>
                  <li>Notify Agent immediately if platform access is revoked or restricted</li>
                </ul>

                <h4 className="text-slate-900 font-semibold mt-6">Email Communications</h4>
                <p>
                  For Grubhub disputes, Customer authorizes Agent to send emails on Customer's behalf using
                  the format: "[Restaurant Name] via RecoverRefunds" with Customer's email as the Reply-To address.
                </p>
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    I have read and agree to the Agency Authorization Agreement. I understand that RecoverRefunds will
                    submit disputes on my behalf and I accept responsibility for all authorized activities.
                  </span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowAgreement(false);
                  setAgreementAccepted(false);
                }}
                className="px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={handleAuthorize}
                disabled={!agreementAccepted}
                className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl text-sm font-medium hover:from-emerald-700 hover:to-emerald-600 shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Accept & Authorize
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationSettings() {
  return (
    <div>
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-900">Notification Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Choose when and how you want to be notified</p>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-4">Email Notifications</h3>
          <div className="space-y-4">
            <SettingToggle
              title="New Flagged Orders"
              description="Get notified when new orders are flagged for review"
              defaultEnabled={true}
            />
            <SettingToggle
              title="Dispute Outcomes"
              description="Receive updates on dispute wins and losses"
              defaultEnabled={true}
            />
            <SettingToggle
              title="Weekly Summary"
              description="Get a weekly report of your recovery metrics"
              defaultEnabled={true}
            />
            <SettingToggle
              title="High Risk Alerts"
              description="Immediate alerts for stores with high refund rates"
              defaultEnabled={false}
            />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-medium text-slate-900 mb-4">Alert Thresholds</h3>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">
              Alert when store refund rate exceeds
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                defaultValue={5}
                className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  return (
    <div>
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-900">Security Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Manage your password and security options</p>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-4">Change Password</h3>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
              <input
                type="password"
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
              <input
                type="password"
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
              <input
                type="password"
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button className="px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
              Update Password
            </button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-medium text-slate-900 mb-4">Two-Factor Authentication</h3>
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheckIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Two-Factor Authentication</p>
              <p className="text-sm text-slate-500 mt-1">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <span className="inline-flex items-center mt-3 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium">
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface BillingStats {
  plan: 'free' | 'pro' | 'enterprise';
  period_start: string;
  period_end: string;
  active_stores: number;
  max_stores: number;
  disputes_submitted: number;
  disputes_won: number;
  disputes_lost: number;
  total_recovered: number;
  success_fee_rate: number;
  success_fee_amount: number;
  win_rate: number;
}

interface BillingSettingsProps {
  checkoutSuccess: boolean;
  checkoutCanceled: boolean;
}

function BillingSettings({ checkoutSuccess, checkoutCanceled }: BillingSettingsProps) {
  const [billingStats, setBillingStats] = useState<BillingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBillingStats();
  }, [checkoutSuccess]); // Refetch when checkout succeeds

  const fetchBillingStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/billing/summary', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch billing data');
      }

      const data = await response.json();
      setBillingStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getPlanDisplay = (plan: string) => {
    switch (plan) {
      case 'pro':
        return { name: 'Pro', fee: '25%', color: 'bg-indigo-100 text-indigo-700' };
      case 'enterprise':
        return { name: 'Enterprise', fee: '20%', color: 'bg-purple-100 text-purple-700' };
      default:
        return { name: 'Free (Beta)', fee: '30%', color: 'bg-blue-100 text-blue-700' };
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-slate-100 rounded-xl" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-20 bg-slate-100 rounded-xl" />
            <div className="h-20 bg-slate-100 rounded-xl" />
            <div className="h-20 bg-slate-100 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-50 rounded-xl border border-red-100 text-red-700">
          Failed to load billing data: {error}
        </div>
      </div>
    );
  }

  const planDisplay = getPlanDisplay(billingStats?.plan || 'free');

  return (
    <div>
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-900">Billing & Subscription</h2>
        <p className="text-sm text-slate-500 mt-0.5">Manage your subscription and payment methods</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Checkout Success Message */}
        {checkoutSuccess && (
          <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <CheckCircleIcon className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-emerald-800">Payment successful!</p>
              <p className="text-sm text-emerald-700">Your subscription has been upgraded. Thank you for your business!</p>
            </div>
          </div>
        )}

        {/* Checkout Canceled Message */}
        {checkoutCanceled && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
            <XCircleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Checkout canceled</p>
              <p className="text-sm text-amber-700">No changes were made to your subscription. You can try again anytime.</p>
            </div>
          </div>
        )}

        {/* Current Plan */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2', planDisplay.color)}>
                Current Plan
              </span>
              <h3 className="text-lg font-semibold text-slate-900">{planDisplay.name}</h3>
              <p className="text-sm text-slate-600 mt-1">
                {billingStats?.plan === 'free'
                  ? `Free during beta period + ${billingStats?.success_fee_rate || 30}% success fee on recovered funds`
                  : `${planDisplay.fee} success fee on recovered funds`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">$0</p>
              <p className="text-sm text-slate-500">/month</p>
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-4">Current Period Usage</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500 mb-1">Active Stores</p>
              <p className="text-xl font-semibold text-slate-900">
                {billingStats?.active_stores || 0} / {billingStats?.max_stores || 5}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500 mb-1">Disputes Submitted</p>
              <p className="text-xl font-semibold text-slate-900">{billingStats?.disputes_submitted || 0}</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl">
              <p className="text-xs text-emerald-600 mb-1">Total Recovered</p>
              <p className="text-xl font-semibold text-emerald-700">
                {formatCurrency(billingStats?.total_recovered || 0)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-xs text-blue-600 mb-1">Success Fee Due</p>
              <p className="text-xl font-semibold text-blue-700">
                {formatCurrency(billingStats?.success_fee_amount || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Win Rate */}
        {billingStats && billingStats.disputes_submitted > 0 && (
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">Win Rate</p>
              <p className="text-sm font-semibold text-slate-900">{billingStats.win_rate}%</p>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-emerald-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(billingStats.win_rate, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>{billingStats.disputes_won} won</span>
              <span>{billingStats.disputes_lost} lost</span>
            </div>
          </div>
        )}

        {/* Upgrade Prompt - only show for free plan */}
        {billingStats?.plan === 'free' && (
          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Upgrade to Pro for lower fees</p>
                <p className="text-sm text-amber-700 mt-1">
                  Pro plans start at $99/month with a reduced 25% success fee (vs 30% on Free). Perfect for growing restaurant groups.
                </p>
                <button className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors">
                  View Plans
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingToggleProps {
  title: string;
  description: string;
  defaultEnabled: boolean;
}

function SettingToggle({ title, description, defaultEnabled }: SettingToggleProps) {
  const [enabled, setEnabled] = useState(defaultEnabled);

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => setEnabled(!enabled)}
        className={clsx(
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          enabled ? 'bg-blue-600' : 'bg-slate-200'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            enabled ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
}
